<template>
  <div
    :class="$style.container"
  >
    <JumpcloudBrandmark
      :class="$style.logo"
      color="jcSecondary"
      :scale="2"
      :type="brandmarkImage"
    />
    <HeadingLarge
      :class="$style.heading"
    >
      {{ welcomeHeading }}
    </HeadingLarge>
    <ValidationObserver
      ref="observer"
      v-slot="{ handleSubmit, invalid }"
      isDisabled
      novalidate
      slim
    >
      <form
        :class="$style.formContainer"
        @keydown.enter.prevent="handleSubmit(submitForm)"
        @submit.prevent.stop="handleSubmit(submitForm)"
      >
        <Grid
          :class="$style.customGrid"
          :hasGap="false"
        >
          <GridItem :gridSpan="6">
            <InputText
              v-model="user.firstName"
              autofocus
              :class="$style.inputFirst"
              :data-test-id="$testId('FirstName')"
              :label="inputFields.firstName.label"
              large
              :name="inputFields.firstName.name"
              rules="required|max:40"
              @blur="sendInputEnteredEvent(inputFields.firstName)"
              @input="initializeFullStory"
            />
          </GridItem>
          <GridItem :gridSpan="6">
            <InputText
              v-model="user.lastName"
              :class="$style.inputLast"
              :data-test-id="$testId('LastName')"
              :label="inputFields.lastName.label"
              large
              :name="inputFields.lastName.name"
              rules="required|max:80"
              @blur="sendInputEnteredEvent(inputFields.lastName)"
              @input="initializeFullStory"
            />
          </GridItem>
          <GridItem :gridSpan="12">
            <PartnerPicker
              v-if="!isMsp"
              :class="$style.partnerPicker"
              :data-test-id="$testId('PartnerPicker')"
              dataTestIdPrefix="OnePageForm"
              selectClass="OnePageForm__PartnerPickerFields"
              @input="initializeFullStory"
            />
          </GridItem>
          <GridItem :gridSpan="6">
            <InputText
              v-model="user.companyName"
              :class="$style.company"
              :data-test-id="$testId('CompanyName')"
              isFullWidth
              :label="inputFields.companyName.label"
              large
              :name="inputFields.companyName.name"
              rules="required|max:80"
              @blur="sendInputEnteredEvent(inputFields.companyName)"
              @input="initializeFullStory"
            />
          </GridItem>
          <GridItem :gridSpan="6">
            <Select
              v-model="user.companySize"
              :class="$style.selectSize"
              :data-test-id="$testId('CompanySize')"
              :label="inputFields.companySize.label"
              large
              :name="inputFields.companySize.name"
              :options="options.companySizeOptions"
              rules="required"
              @blur="sendInputEnteredEvent(inputFields.companySize)"
              @input="initializeFullStory"
            />
          </GridItem>
          <GridItem :gridSpan="6">
            <CountryPicker />
          </GridItem>
          <GridItem :gridSpan="6">
            <StatePicker />
            <DataCenterPicker
              v-if="signupDataCenterSelection"
            />
          </GridItem>
          <GridItem :gridSpan="6">
            <ValidationObserver
              ref="emailObserver"
              slim
            >
              <InputText
                ref="email"
                v-model="user.email"
                :class="$style.input"
                :data-test-id="$testId('Email')"
                :label="inputFields.email.label"
                large
                mode="passive"
                :name="inputFields.email.name"
                required
                rules="email|noPersonalEmail|max:80"
                type="email"
                @blur="validateEmail(inputFields.email)"
                @input="initializeFullStory"
              />
            </ValidationObserver>
          </GridItem>
          <GridItem :gridSpan="12">
            <PhoneNumber
              :class="$style.phoneNumber"
              :data-test-id="$testId('PhoneNumber')"
              @input="initializeFullStory"
            />
          </GridItem>
        </Grid>
        <TermsOfServiceCheck @handleClick="validateRecaptcha" />
        <div :class="$style.footer">
          <div>
            <p
              v-if="registerError"
              :class="$style.error"
            >
              {{ registerError }}
            </p>
            <RecaptchaInvisible v-model="user.recaptchaToken" />
          </div>
          <div :class="$style.footerCtas">
            <Button
              :class="$style.submit"
              data-test-id="OnePageForm__Submit"
              :isDisabled="invalid || invalidCountrySelected"
              :isLoading="loading"
              isMarketingStyle
              text="Create Account"
              @click.native="handleSubmit(submitForm)"
            />
            <LoginLinkNew />
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import * as FS from '@fullstory/browser';
import {
  Button,
  Grid,
  GridItem,
  HeadingLarge,
  InputText,
  JumpcloudBrandmark,
  Select,
} from '@jumpcloud/ui-components';
import { ValidationObserver, validate } from 'vee-validate';
import { mapActions, mapGetters, mapState } from 'vuex';
import CountryPicker from 'client/signup/components/inputs/CountryPicker.vue';
import DataCenterPicker from '../components/inputs/DataCenterPicker.vue';
import FullStory from 'client/util/FullStory';
import LoginLinkNew from 'client/signup/components/LoginLinkNew.vue';
import PartnerPicker from 'client/signup/components/inputs/PartnerPicker.vue';
import PhoneNumber from 'client/signup/components/inputs/PhoneNumber.vue';
import RecaptchaInvisible from 'client/signup/components/RecaptchaInvisible.vue';
import SignupConstants from 'client/util/Constants/SignupConstants';
import SignupOptions from 'client/signup/SignupOptions';
import StatePicker from 'client/signup/components/inputs/StatePicker.vue';
import StoreConfigConstants from 'client/util/Constants/StoreConfigConstants';
import TermsOfServiceCheck from 'client/signup/components/inputs/TermsOfServiceCheck.vue';

const { recaptchaState } = StoreConfigConstants;
const { signupDisabledCountries, SignupHeading } = SignupConstants;

export default {
  name: 'OnePageForm',

  components: {
    Button,
    CountryPicker,
    DataCenterPicker,
    Grid,
    GridItem,
    HeadingLarge,
    InputText,
    JumpcloudBrandmark,
    LoginLinkNew,
    PartnerPicker,
    PhoneNumber,
    RecaptchaInvisible,
    Select,
    StatePicker,
    TermsOfServiceCheck,
    ValidationObserver,
  },

  props: {
    isMsp: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hasRecaptchaRendered: false,
      loading: false,
      options: SignupOptions,
      inputFields: {
        firstName: {
          label: 'First Name',
          name: 'first name',
        },
        lastName: {
          label: 'Last Name',
          name: 'last name',
        },
        companyName: {
          label: 'Company Name',
          name: 'company name',
        },
        companySize: {
          label: 'Company Size',
          name: 'company size',
        },
        email: {
          label: 'Company Email',
          name: 'email',
        },
      },
    };
  },

  computed: {
    ...mapGetters('ExperimentsModule', [
      'getIsExperimentVariation',
    ]),

    ...mapState([
      'hasEmailQueryParam',
      'hasLoaded',
      'recaptchaLoaded',
      'recaptchaExpired',
      'registerError',
      'user',
    ]),

    brandmarkImage() {
      if (this.isMsp) {
        return 'jcLogoMsp';
      }

      return 'jcLogoWordmark';
    },

    invalidCountrySelected() {
      return signupDisabledCountries.indexOf(this.user.country) !== -1;
    },

    routeQuery() {
      if (this.$route && this.hasLoaded) {
        return this.$route.query;
      }

      return null;
    },

    signupDataCenterSelection() {
      return this.getIsExperimentVariation('signupDataCenterSelection', 'on');
    },

    submissionLink() {
      return `/signup/confirm?email=${encodeURIComponent(this.user.email)}`;
    },

    welcomeHeading() {
      if (this.isMsp) {
        return SignupHeading.msp;
      }

      return SignupHeading.general;
    },
  },

  watch: {
    routeQuery: {
      handler(value) {
        if (value) {
          const { email } = value;

          if (email) {
            this.updateEmail(email);
            this.updateHasEmailQueryParam(true);

            // validate the email
            validate(email, 'email|noPersonalEmail').then((result) => {
              if (!result.valid || !this.user.email || !this.hasEmailQueryParam) {
                // if invalid email param, trigger validation message
                this.$refs.observer.validate();
              }
            });
          }
        }
      },
      deep: true,
      immediate: true,
    },

    user: {
      handler(newValue) {
        if (newValue.recaptchaToken) {
          this.setRecaptchaLoaded(recaptchaState.DONE_LOADING);
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.updateHasEmailQueryParam(false);
    if (this.isMsp) {
      this.user.isPartner = 'Yes';
      this.user.partnerType = 'MSP';
    }

    this.initializeTargetEnv();
  },

  methods: {
    ...mapActions([
      'sendInputEnteredEvent',
      'setRecaptchaLoaded',
      'setRecaptchaExpired',
      'setValidation',
      'submitRegistration',
      'updateEmail',
      'updateEmailValid',
      'updateHasEmailQueryParam',
      'initializeTargetEnv',
    ]),

    initializeFullStory(value) {
      // Initializes FullStory for direct signups
      if (value && !FS.isInitialized()) {
        FullStory.initialize();
      }
    },

    submitForm() {
      if (this.invalidCountrySelected) return;
      this.setValidation(true);

      const { recaptchaToken } = this.user;

      this.validateRecaptcha();

      if (recaptchaToken) {
        this.setRecaptchaLoaded(recaptchaState.DONE_LOADING);
      } else {
        this.setRecaptchaLoaded(recaptchaState.ERROR_LOADING);
      }

      if (recaptchaToken && !this.loading) {
        this.loading = true;
        this.submitRegistration(this.submissionLink)
          .then((hasError) => {
            // registration attempt had an error
            // flip loading state so that they can try to submit again
            if (hasError) {
              this.loading = false;
            }
          });
      }
    },

    validateEmail(emailInput) {
      this.$refs.emailObserver.validate().then((result) => {
        this.updateEmailValid(result);
        this.sendInputEnteredEvent(emailInput);
      });
    },

    validateRecaptcha() {
      if (window && window.grecaptcha && this.recaptchaExpired) {
        window.grecaptcha.execute();
        this.setRecaptchaExpired(false);
      }
    },
  },
};
</script>

<style module scoped>
@value (max-width-medium) from '~css/breakpoints.css';

  .container {
    margin: var(--jc-spacer-medium);
  }

  .container .heading.heading {
    margin-bottom: var(--jc-size-3);
    text-align: center;
  }

  .container .logo.logo {
    display: block;
    margin-bottom: var(--jc-spacer-medium);
    margin-top: var(--jc-size-3);
    max-width: 100%;
    text-align: center;
  }

  .customGrid {
    column-gap: var(--jc-size-2p25);
    padding-bottom: var(--jc-spacer-small);
    row-gap: var(--jc-spacer-small);
  }

  .hidden {
    display: none;
  }

  .partnerPicker {
    display: flex;
    justify-content: space-between;
  }

  /* Modifies styling on partner picker and phone component, can be moved within
   the component once we clean up feature flag that is requiring both versions */
  .partnerPicker >>> :global(.OnePageForm__PartnerPickerFields) {
    flex-basis: calc(50% - 18px);
  }

  .phoneNumber.phoneNumber {
    border-bottom: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .footer {
    align-items: center;
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  .footerCtas {
    align-self: flex-end;
  }

  .submit.submit {
    display: block;
    margin: var(--jc-spacer-medium) 0 var(--jc-spacer) auto;
  }

  .footer >>> :global(.g-recaptcha) {
    left: 0;
    position: absolute;
  }

  .error {
    color: var(--jcError);
  }

  .recaptchaErrorButton {
    background: none;
    border: 0;
    color: var(--jcPrimary);
    cursor: pointer;
    font-weight: 600;
    padding: 0;
  }

  @media screen and (max-width: max-width-medium) {
    .customGrid {
      column-gap: 0;
    }

    .partnerPicker {
      flex-direction: column;
    }

    .footer {
      flex-direction: column;
    }
  }
</style>
